/**
 * nameTokens.ts — splits a display name into the word tokens an avatar should
 * actually draw initials from.
 *
 * WHY: every identity avatar in the app derived initials with
 * `name.trim().split(/\s+/)` and then took `part[0]`, with no check that the
 * character was a letter. Seeded rows are prefixed with a tag — `[SEED] Colt
 * Tester`, `[SEEDV] Driver 01` — so the first "word" is `[SEED]` and the first
 * "initial" is `[`. The signed-in user's own avatar read "[C".
 *
 * Two rules, both about tags rather than about brackets specifically:
 *   1. A token wrapped in brackets is a tag, not part of the name — drop it
 *      whole. Stripping only the punctuation would turn `[SEED]` into `SEED`
 *      and yield "SC", which is no better than "[C".
 *   2. Leading/trailing punctuation on a real word is noise — strip it, so
 *      `"Colt"` or `(Colt)` still gives `C`. Interior punctuation is kept:
 *      `O'Brien` and `Jean-Luc` are single words with a real first letter.
 *
 * Callers keep their own selection rule (first-two words vs first-and-last) —
 * this only decides what counts as a word.
 */

/** `[SEED]`, `(inactive)`, `<draft>` — a bracketed tag, not a name. */
const TAG_TOKEN = /^[[({<][^\])}>]*[\])}>]$/;

/** Trim anything that is not a letter or digit from both ends of a word. */
function stripEdgePunctuation(token: string): string {
  return token.replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^\p{L}\p{N}]+$/u, '');
}

/**
 * Word tokens suitable for initials. Returns `[]` when nothing usable remains
 * (e.g. a name that is only a tag), which every caller already handles by
 * falling back to '?'.
 */
export function nameTokens(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0 && !TAG_TOKEN.test(token))
    .map(stripEdgePunctuation)
    .filter((token) => token.length > 0);
}
